import React, { Fragment, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import allProjects from '../../data/ca_housing_pipeline.json';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import PipelineExplorer from '../../components/PipelineExplorer/PipelineExplorer';
import PipelineCTA from '../../components/PipelineCTA/PipelineCTA';
import { doorFeeIncome } from '../../data/doorFee';

const money = (n) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(1) + 'M' : '$' + Math.round(n / 1e3) + 'K');

// Keyword-rich, human-written context per housing type (avoids thin/templated pages).
const TYPE_BLURB = {
    'large-family': 'Large-family affordable housing serves households with children, typically with a higher share of two-, three-, and four-bedroom units. These are among the most common LIHTC developments in California.',
    'senior': 'Senior affordable housing serves older adults, often with age-restricted occupancy, accessibility features, and on-site services. Technology scope frequently includes emergency call, secure access, and community-area connectivity.',
    'special-needs': 'Special-needs affordable housing serves residents requiring supportive services — including people with disabilities and those exiting homelessness — often paired with on-site case management.',
    'at-risk': 'At-risk developments preserve existing affordable housing whose affordability covenants or subsidies are expiring, keeping units affordable through acquisition and rehabilitation.',
    'sro': 'Single-room-occupancy (SRO) housing provides compact, deeply affordable units — a key tool for housing individuals at the lowest income levels and those exiting homelessness.',
    'non-targeted': 'Non-targeted developments serve a general affordable-housing population without a specific demographic restriction, offering a mix of unit types and income tiers.',
};

const HousingTypePage = () => {
    const { typeSlug } = useParams();

    const projects = useMemo(
        () => allProjects.filter((p) => p.housingTypeSlug === typeSlug),
        [typeSlug]
    );
    const typeName = projects[0]?.housingType || '';

    const stats = useMemo(() => {
        let units = 0, nine = 0, four = 0, doorFeeTotal = 0;
        const counties = new Set();
        projects.forEach((p) => {
            units += p.totalUnits || 0;
            doorFeeTotal += doorFeeIncome(p.totalUnits);
            if (p.creditType === '9%') nine++; else if (p.creditType === '4%') four++;
            counties.add(p.county);
        });
        return { units, nine, four, doorFeeTotal, counties: counties.size };
    }, [projects]);

    usePageSeo({
        title: `${typeName} Affordable Housing in CA — LIHTC`,
        description: `New ${typeName.toLowerCase()} affordable housing developments across California — ${projects.length} LIHTC projects (9% and 4% tax credits) with builders, unit counts, and locations, from CTCAC data.`,
        keywords: `${typeName} affordable housing California, ${typeName} LIHTC, ${typeName.toLowerCase()} housing developers, new ${typeName.toLowerCase()} multifamily, 9% 4% tax credit ${typeName.toLowerCase()}`,
    });

    if (!typeName) return <Navigate to="/housing-pipeline" replace />;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${typeName} Affordable Housing Pipeline — California`,
        numberOfItems: projects.length,
        itemListElement: projects.slice(0, 50).map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: p.name })),
    };

    const StatCard = ({ value, label }) => (
        <div className="col-6 col-lg mb-3">
            <div className="text-center p-3 h-100" style={{ background: '#fff', border: '1px solid #ead9cd', borderRadius: 14 }}>
                <div className="h2 mb-0" style={{ color: '#B85C38' }}>{value}</div>
                <div className="small text-muted">{label}</div>
            </div>
        </div>
    );

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={`${typeName} Affordable Housing in California`} pagesub={typeName} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

                        <nav aria-label="breadcrumb" className="mb-3">
                            <Link to="/housing-pipeline">California Pipeline</Link>
                            <span className="text-muted"> / {typeName}</span>
                        </nav>

                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">{typeName} affordable housing across California</h2>
                                <p>
                                    California's pipeline includes <strong>{projects.length} {typeName.toLowerCase()} affordable housing
                                    project{projects.length === 1 ? '' : 's'}</strong> across {stats.counties} counties — {stats.nine} funded
                                    with 9% tax credits and {stats.four} with 4% credits, totaling roughly <strong>{stats.units.toLocaleString()} units</strong>.
                                    Search by builder or filter by county and credit type below.
                                </p>
                                <p>{TYPE_BLURB[typeSlug] || ''}</p>
                                <p className="text-muted">
                                    BUILDLAB designs the low-voltage, security, and life-safety systems behind developments like
                                    these — structured cabling, surveillance, door access, and fire &amp; life safety, engineered in-house.
                                </p>
                            </div>
                        </div>

                        <div className="row mb-5">
                            <StatCard value={projects.length} label="Projects" />
                            <StatCard value={stats.units.toLocaleString()} label="Total units" />
                            <StatCard value={money(stats.doorFeeTotal)} label="Potential door-fee income" />
                            <StatCard value={`${stats.nine} / ${stats.four}`} label="9% / 4% credit" />
                            <StatCard value={stats.counties} label="Counties" />
                        </div>

                        <PipelineExplorer projects={projects} showCountyFilter={true} />

                        <PipelineCTA context={`${typeName.toLowerCase()} housing`} />

                        <p className="small text-muted mt-4">
                            Data source: California Tax Credit Allocation Committee (CTCAC). Map &copy; OpenStreetMap contributors.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default HousingTypePage;
